import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Layout from '../components/Layout';

// ─── Feature card definitions ─────────────────────────────────────────────────

const FEATURES = [
  {
    id:               'study',
    title:            'Study Assistant',
    desc:             'Ask questions about your course topics. Get personalized explanations and guidance tailored to your enrolled course.',
    icon:             'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
    route:            '/student/careerai/study',
    badge:            'LMS Students Only',
    badgeCls:         'bg-secondary/10 text-secondary',
    needsActivation:  true,
    comingSoon:       false,
  },
  {
    id:               'resume',
    title:            'Resume Builder',
    desc:             'Build a professional ATS-friendly resume from your profile in seconds.',
    icon:             'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    badge:            'Coming Soon',
    badgeCls:         'bg-gray-100 text-gray-400',
    needsActivation:  false,
    comingSoon:       true,
  },
  {
    id:               'interview',
    title:            'Mock Interview',
    desc:             'Practice interview questions for your target role with personalised AI feedback.',
    icon:             'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
    badge:            'Coming Soon',
    badgeCls:         'bg-gray-100 text-gray-400',
    needsActivation:  false,
    comingSoon:       true,
  },
  {
    id:               'jobs',
    title:            'Job Alerts',
    desc:             'Get personalised job matches based on your profile and target role.',
    icon:             'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    badge:            'Coming Soon',
    badgeCls:         'bg-gray-100 text-gray-400',
    needsActivation:  false,
    comingSoon:       true,
  },
];

const isProfileComplete = (p) =>
  p && Boolean(p.target_role) && Array.isArray(p.skills) && p.skills.length >= 2;

// ─── Main Component ───────────────────────────────────────────────────────────

const CareerAIDashboard = () => {
  const navigate       = useNavigate();
  const { user }       = useAuth();

  const [profile,      setProfile]      = useState(null);
  const [usage,        setUsage]        = useState(null);   // null = unknown / not activated
  const [notActivated, setNotActivated] = useState(false);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    let done = 0;
    const finish = () => { done++; if (done === 2) setLoading(false); };

    // Profile
    api.get('/api/ai/profile')
      .then((res) => setProfile(res.data))
      .catch(() => {}) // 404 = no profile yet, silently ignore
      .finally(finish);

    // Usage — 403 = CareerAI not activated for this student
    api.get('/api/ai/study/usage-today')
      .then((res) => setUsage(res.data))
      .catch((err) => { if (err.response?.status === 403) setNotActivated(true); })
      .finally(finish);
  }, []);

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl space-y-6 animate-pulse">
          <div className="skeleton h-8 w-40" />
          <div className="skeleton h-4 w-56" />
          <div className="skeleton h-28 rounded-2xl" />
          <div className="skeleton h-20 rounded-2xl" />
          <div className="skeleton h-5 w-28" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-48 rounded-2xl" />)}
          </div>
        </div>
      </Layout>
    );
  }

  const complete   = isProfileComplete(profile);
  const topSkills  = (profile?.skills || []).slice(0, 3);
  const extraCount = (profile?.skills?.length || 0) - 3;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="max-w-4xl space-y-6 animate-fade-in">

        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CareerAI</h1>
          <p className="text-sm text-gray-500 mt-1">Your AI-powered career assistant</p>
        </div>

        {/* ── Profile status card ── */}
        {complete ? (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-semibold text-gray-900 truncate">{user?.full_name || 'Your Profile'}</p>
                  <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    Complete
                  </span>
                </div>
                {profile.target_role && (
                  <p className="text-sm text-gray-600">
                    Target: <span className="font-medium text-emerald-700">{profile.target_role}</span>
                  </p>
                )}
                {topSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {topSkills.map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-lg font-medium">{s}</span>
                    ))}
                    {extraCount > 0 && (
                      <span className="text-xs text-gray-500">+{extraCount} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/student/careerai/profile')}
              className="flex-shrink-0 text-sm font-medium text-emerald-700 hover:text-emerald-900 transition-colors whitespace-nowrap"
            >
              Edit Profile →
            </button>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Complete your profile</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Get personalised AI assistance tailored to your career goals.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/student/careerai/profile')}
              className="flex-shrink-0 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:bg-amber-600 active:scale-[0.98] transition-all whitespace-nowrap"
            >
              Complete Profile
            </button>
          </div>
        )}

        {/* ── Usage card ── */}
        {notActivated ? (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-700 text-sm">CareerAI Not Activated</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Contact your admin to activate CareerAI access for your account.
              </p>
            </div>
          </div>
        ) : usage ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-900">Study Assistant Usage Today</p>
              <span className="text-xs font-medium text-gray-500">
                {usage.usage_today} / {usage.usage_limit} questions
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-primary rounded-full h-2.5 transition-all duration-500"
                style={{ width: `${Math.min(100, (usage.usage_today / Math.max(1, usage.usage_limit)) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {usage.remaining} question{usage.remaining !== 1 ? 's' : ''} remaining · Resets at midnight UTC
            </p>
          </div>
        ) : null}

        {/* ── Feature cards ── */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 mb-4">AI Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map((feat) => {
              const isLocked = feat.needsActivation && (notActivated || !usage);

              return (
                <div
                  key={feat.id}
                  className={`bg-white border rounded-2xl p-5 flex flex-col transition-all duration-200 ${
                    feat.comingSoon
                      ? 'border-gray-200 opacity-60'
                      : isLocked
                      ? 'border-gray-200'
                      : 'border-gray-200 hover:border-primary hover:shadow-md'
                  }`}
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      feat.comingSoon || isLocked ? 'bg-gray-100' : 'bg-primary/10'
                    }`}>
                      {isLocked && !feat.comingSoon ? (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      ) : (
                        <svg
                          className={`w-5 h-5 ${feat.comingSoon ? 'text-gray-400' : 'text-primary'}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={feat.icon} />
                        </svg>
                      )}
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${feat.badgeCls}`}>
                      {feat.badge}
                    </span>
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1">{feat.title}</h3>
                  <p className="text-sm text-gray-500 flex-1 leading-relaxed">{feat.desc}</p>

                  {/* Card footer */}
                  <div className="mt-4">
                    {feat.comingSoon ? (
                      <p className="text-xs text-gray-400 font-medium">Coming soon</p>
                    ) : isLocked ? (
                      <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Requires activation by admin
                      </p>
                    ) : (
                      <button
                        onClick={() => navigate(feat.route)}
                        className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all"
                      >
                        Launch →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CareerAIDashboard;
